import { memo, useMemo } from 'react';
import { Entity } from '@mainstage/catalog-model';
import { WitboostSystem } from '@agilelab/plugin-wb-builder-common';
import {
  MainPageType,
  useDynamicBreadcrumbLink,
  WbBreadcrumb,
  WbBreadcrumbs,
} from '@agilelab/plugin-wb-platform';
import { useLocation } from 'react-router';
import { useRouteRef } from '@mainstage/core-plugin-api';
import { rootRouteRef } from '../../routes';
import { entityRouteRef } from '@mainstage/plugin-catalog-react';

interface EntityBreadcrumbsProps {
  entity?: Entity;
  allowed: boolean;
  dpEntity?: Entity;
  headerTitle: string;
}

const EntityBreadcrumbs = memo(
  ({ entity, allowed, dpEntity, headerTitle }: EntityBreadcrumbsProps) => {
    const currentRoute = useLocation().pathname;

    const catalogRouteRef = useRouteRef(rootRouteRef);
    const catalogEntityRoute = useRouteRef(entityRouteRef);

    const myProjectsLinkWithQueryString = useDynamicBreadcrumbLink(
      catalogRouteRef,
      MainPageType.BUILDER,
    );
    const adminCatalogLinkWithQueryString = useDynamicBreadcrumbLink(
      catalogRouteRef,
      MainPageType.ADMINISTRATION,
    );

    const catalogPrefixRoute = currentRoute.startsWith('/platform-settings')
      ? '/platform-settings'
      : '';
    const isAdminRoute = currentRoute.startsWith('/platform-settings');

    const firstLevelBreadcrumb = useMemo(() => {
      return isAdminRoute ? (
        <WbBreadcrumb>Administration</WbBreadcrumb>
      ) : (
        <WbBreadcrumb>Builder</WbBreadcrumb>
      );
    }, [isAdminRoute]);

    const adminCategoryBreadcrumb = useMemo(() => {
      if (!isAdminRoute || !entity) return null;

      const templateKinds = [
        'Template',
        'Blueprint',
        'EditTemplate',
        'ReverseProvisioningTemplate',
        'SystemPrototypeTemplate',
        'AccessControlRequestTemplate',
      ];
      const userGroupKinds = ['User', 'Group'];

      if (templateKinds.includes(entity.kind)) {
        return (
          <WbBreadcrumb to="/platform-settings/templates">
            Templates
          </WbBreadcrumb>
        );
      } else if (userGroupKinds.includes(entity.kind)) {
        return <WbBreadcrumb to="/platform-settings/iam">IAM</WbBreadcrumb>;
      }
      return (
        <WbBreadcrumb
          to={allowed ? adminCatalogLinkWithQueryString : undefined}
        >
          Entities
        </WbBreadcrumb>
      );
    }, [isAdminRoute, entity, allowed, adminCatalogLinkWithQueryString]);

    const projectsBreadcrumb = useMemo(() => {
      if (isAdminRoute || !entity) return null;

      const templateKinds = [
        'Template',
        'Blueprint',
        'EditTemplate',
        'ReverseProvisioningTemplate',
        'SystemPrototypeTemplate',
        'AccessControlRequestTemplate',
      ];

      if (templateKinds.includes(entity.kind)) {
        return (
          <WbBreadcrumb to={myProjectsLinkWithQueryString}>
            Templates
          </WbBreadcrumb>
        );
      }

      return (
        <WbBreadcrumb to={myProjectsLinkWithQueryString}>
          My projects
        </WbBreadcrumb>
      );
    }, [isAdminRoute, myProjectsLinkWithQueryString, entity]);

    const dataProductBreadcrumb = useMemo(() => {
      if (!dpEntity) return null;

      const dpLink = `${catalogPrefixRoute}${catalogEntityRoute({
        kind: dpEntity.kind,
        namespace: dpEntity.metadata.namespace || 'default',
        name: dpEntity.metadata.name,
      })}`;

      return (
        <WbBreadcrumb to={dpLink}>
          {(dpEntity as WitboostSystem)?.spec?.mesh?.name}
        </WbBreadcrumb>
      );
    }, [dpEntity, catalogPrefixRoute, catalogEntityRoute]);

    const entityNameBreadcrumb = useMemo(() => {
      return (
        <WbBreadcrumb>
          {(entity as WitboostSystem)?.spec?.mesh?.name || headerTitle}
        </WbBreadcrumb>
      );
    }, [entity, headerTitle]);

    return (
      <WbBreadcrumbs aria-label="breadcrumb">
        {firstLevelBreadcrumb}
        {adminCategoryBreadcrumb}
        {projectsBreadcrumb}
        {dataProductBreadcrumb}
        {entityNameBreadcrumb}
      </WbBreadcrumbs>
    );
  },
);

EntityBreadcrumbs.displayName = 'EntityBreadcrumbs';

export default EntityBreadcrumbs;
